/**
 * Error handling utilities for consistent error processing across the application.
 *
 * **IMPORTANT**: Always use `logError()` instead of `logger.error()` directly when logging errors.
 * This ensures errors are automatically sent to Sentry with proper context.
 */

import { logger, type LogContext } from '../logger';
import { EXPIRED_SESSION_ERROR_PATTERNS } from './response-utils';

/**
 * Extract error message from unknown error type.
 *
 * @param error - Error of unknown type
 * @returns String representation of the error message
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message);
  }
  return String(error);
}

/**
 * Get Error object for logging, converting non-Error values to Error instances.
 * This ensures errors can be properly sent to Sentry.
 *
 * @param error - Error of unknown type
 * @returns Error instance (converts non-Error values to Error)
 */
export function getErrorForLogging(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  const errorMessage = getErrorMessage(error);
  return new Error(errorMessage);
}

/**
 * Log an error with automatic Sentry reporting.
 * This is the recommended way to log errors - it ensures errors are always sent to Sentry.
 *
 * **Why use this instead of `logger.error()` directly?**
 * - Automatically converts non-Error values to Error objects
 * - Ensures errors are sent to Sentry (requires Error object as 3rd parameter)
 * - Consistent error handling across the codebase
 *
 * **Optional Context Values:**
 * - For optional values like `gmailEmail`, use conditional inclusion: `...(gmailEmail && { gmailEmail })`
 * - This avoids placeholder values and keeps logs clean
 * - The logger automatically filters out `undefined` values
 *
 * @param message - Error message to log
 * @param context - Additional context (userId, requestId, etc.)
 * @param error - Error of unknown type (will be converted to Error if needed)
 *
 * @example
 * ```ts
 * try {
 *   await someOperation();
 * } catch (error) {
 *   // ✅ CORRECT - Always sends to Sentry
 *   logError('Operation failed', { userId, operation: 'someOperation' }, error);
 * }
 * ```
 *
 * @example
 * ```ts
 * // ✅ CORRECT - Conditionally include optional values
 * logError('Error message', {
 *   userId,
 *   ...(gmailEmail && { gmailEmail }), // Only include if truthy
 * }, error);
 *
 * // ⚠️ ACCEPTABLE but less clean - uses placeholder
 * logError('Error message', {
 *   userId,
 *   gmailEmail: gmailEmail || 'primary', // Always includes, uses placeholder
 * }, error);
 * ```
 */
export function logError(message: string, context?: LogContext, error?: unknown): void {
  const errorMessage = error ? getErrorMessage(error) : message;
  const errorObj = error ? getErrorForLogging(error) : new Error(message);

  // Always pass Error object as 3rd parameter to ensure Sentry reporting
  logger.error(message, { ...context, error: errorMessage }, errorObj);
}

/**
 * Type guard to check if value is an Error instance.
 *
 * @param error - Value to check
 * @returns True if value is an Error instance
 *
 * @example
 * ```ts
 * if (isError(error)) {
 *   // TypeScript now knows error is Error
 *   console.log(error.stack);
 * }
 * ```
 */
export function isError(error: unknown): error is Error {
  return error instanceof Error;
}

/**
 * Safely stringify a value to JSON, with size limits and error handling.
 * Prevents errors from circular references or extremely large objects.
 *
 * @param value - Value to stringify
 * @param maxLength - Maximum length of the resulting string (default: 500)
 * @returns Stringified value, truncated if necessary
 *
 * @example
 * ```ts
 * const str = safeStringify(largeObject, 200);
 * // Returns at most 200 characters, with '...' appended if truncated
 * ```
 */
export function safeStringify(value: unknown, maxLength: number = 500): string {
  try {
    const str = JSON.stringify(value);
    if (str.length > maxLength) {
      return str.substring(0, maxLength) + '...';
    }
    return str;
  } catch {
    // Fallback to String() if JSON.stringify fails (circular references, etc.)
    const str = String(value);
    return str.length > maxLength ? str.substring(0, maxLength) + '...' : str;
  }
}

/**
 * Convert a promise rejection reason to an Error instance.
 * Handles various rejection types (Error, string, object, etc.)
 *
 * @param reason - Promise rejection reason
 * @returns Error instance
 *
 * @example
 * ```ts
 * const error = convertRejectionToError('Invalid data');
 * // Returns: Error('Invalid data')
 * ```
 */
export function convertRejectionToError(reason: unknown): Error {
  if (reason instanceof Error) {
    return reason;
  }

  if (typeof reason === 'string') {
    // Handle specific error pattern commonly seen in production
    // "Object Not Found Matching Id:X, MethodName:update, ParamCount:Y"
    const match = reason.match(
      /Object Not Found Matching Id:(\d+), MethodName:(\w+), ParamCount:(\d+)/
    );
    if (match) {
      const [, id, methodName, paramCount] = match;
      const error = new Error(`Object not found (ID: ${id}) during ${methodName} operation`);
      error.name = 'ObjectNotFoundError';
      // Store details for Sentry to pick up
      (error as Error & { context?: Record<string, unknown> }).context = {
        id,
        methodName,
        paramCount,
        originalReason: reason,
      };
      return error;
    }

    return new Error(reason);
  }

  if (reason && typeof reason === 'object' && 'message' in reason) {
    return new Error(String(reason.message));
  }

  return new Error(`Unhandled promise rejection: ${String(reason)}`);
}

/**
 * Get context information about a promise rejection.
 *
 * @param reason - Promise rejection reason
 * @returns Object with rejection type and value
 */
export function getRejectionContext(reason: unknown): {
  rejectionType: string;
  rejectionValue: string | number | undefined;
} {
  const rejectionType =
    reason instanceof Error
      ? 'Error'
      : typeof reason === 'string'
        ? 'string'
        : typeof reason === 'object'
          ? 'object'
          : typeof reason;

  const rejectionValue =
    reason instanceof Error
      ? undefined
      : typeof reason === 'string' || typeof reason === 'number'
        ? reason
        : typeof reason === 'object'
          ? safeStringify(reason, 200)
          : undefined;

  return { rejectionType, rejectionValue };
}

/**
 * Sets up global unhandled rejection handler for Sentry.
 * Converts non-Error rejections to Error instances for proper reporting.
 *
 * **Important**: This should only be called once during application initialization.
 * It's automatically called in `instrumentation-client.ts`.
 *
 * @param Sentry - Sentry instance from '@sentry/nextjs'
 *
 * @example
 * ```ts
 * import * as Sentry from '@sentry/nextjs';
 * setupUnhandledRejectionHandler(Sentry);
 * ```
 */
export function setupUnhandledRejectionHandler(Sentry: typeof import('@sentry/nextjs')): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener('unhandledrejection', (event) => {
    // Convert non-Error rejections to Error instances for proper Sentry reporting
    const error = convertRejectionToError(event.reason);
    const { rejectionType, rejectionValue } = getRejectionContext(event.reason);

    // Check if error has additional context from our conversion
    const additionalContext =
      (error as Error & { context?: Record<string, unknown> }).context || {};

    // Capture to Sentry with additional context
    Sentry.captureException(error, {
      tags: {
        rejectionType,
        source: 'unhandledRejection',
      },
      extra: {
        rejectionValue,
        originalReason: String(event.reason),
        ...additionalContext,
      },
    });

    // Prevent default browser console error (we've handled it)
    // Only in production - in development, let it show in console for debugging
    if (process.env.NODE_ENV === 'production') {
      event.preventDefault();
    }
  });
}

// Re-export EXPIRED_SESSION_ERROR_PATTERNS from the canonical source in response-utils.ts.
// Previously this file had its own stale copy that included bare 'Invalid data' (which
// was incorrectly catching third-party script errors like Crisp chat widget).
export { EXPIRED_SESSION_ERROR_PATTERNS } from './response-utils';

/**
 * Checks if an error indicates an expired session.
 * Uses pattern matching to detect common expired session error messages.
 * Matching is case-insensitive to catch variations like "Your session expired".
 *
 * @param error - Error to check
 * @returns True if the error indicates an expired session, false otherwise
 *
 * @example
 * ```ts
 * try {
 *   await apiRequest();
 * } catch (error) {
 *   if (isExpiredSessionError(error)) {
 *     // Handle expired session
 *     redirectToLogin();
 *   }
 * }
 * ```
 *
 * @see {@link EXPIRED_SESSION_ERROR_PATTERNS} - Error patterns checked
 */
export function isExpiredSessionError(error: unknown): boolean {
  const errorMessage = getErrorMessage(error).toLowerCase();
  return EXPIRED_SESSION_ERROR_PATTERNS.some((pattern) =>
    errorMessage.includes(pattern.toLowerCase())
  );
}

/**
 * Handles expired session errors with user feedback and redirect.
 * Shows a toast notification and redirects to login after a delay.
 *
 * @param error - Error to check and handle
 * @param toast - Toast function from `useToast()` hook
 * @param options - Optional configuration
 * @param options.redirectDelay - Delay before redirect in milliseconds (default: 2000)
 * @param options.redirectPath - Path to redirect to (default: '/')
 * @returns True if the error was an expired session error and was handled, false otherwise
 *
 * @example
 * ```ts
 * const { toast } = useToast();
 *
 * try {
 *   await fetchData();
 * } catch (error) {
 *   if (handleExpiredSessionError(error, toast)) {
 *     return; // Error was handled, don't process further
 *   }
 *   // Handle other errors...
 * }
 * ```
 *
 * @see {@link isExpiredSessionError} - Error detection utility
 */
export function handleExpiredSessionError(
  error: unknown,
  toast: (props: { title: string; description: string; variant: 'destructive' }) => void,
  options?: { redirectDelay?: number; redirectPath?: string }
): boolean {
  if (!isExpiredSessionError(error)) {
    return false;
  }

  const { redirectDelay = 2000, redirectPath = '/' } = options || {};

  toast({
    title: 'Session Expired',
    description: 'Your session has expired. Please refresh the page to log in again.',
    variant: 'destructive',
  });

  // Only redirect if window is available (browser environment)
  if (typeof window !== 'undefined') {
    setTimeout(() => {
      window.location.href = redirectPath;
    }, redirectDelay);
  }

  return true;
}
